import React, { useEffect, useMemo, useState, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../components/ui/sheet';
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogCancel, AlertDialogAction, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Upload, FileText, Sparkles, Download, Eye, History, ScrollText, Trash2,
  PackageOpen, Send, Search, X, FileBadge, Share2, Users,
} from 'lucide-react';
import { EpfBadge } from '../components/EpfBadge';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import DocumentEditor from '../components/DocumentEditor';

const DOC_TYPES = [
  { value: 'offer_letter', label: 'Offer Letter', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'increment',    label: 'Increment',    color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'addendum',     label: 'Addendum',     color: 'bg-purple-100 text-purple-700 border-purple-300' },
  { value: 'confirmation', label: 'Confirmation', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'experience',   label: 'Experience',   color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  { value: 'nda',          label: 'NDA',          color: 'bg-rose-100 text-rose-700 border-rose-300' },
  { value: 'pip',          label: 'PIP',          color: 'bg-orange-100 text-orange-700 border-orange-300' },
];
const GENERATABLE = ['offer_letter', 'increment', 'addendum'];

function typeMeta(t) {
  return DOC_TYPES.find(d => d.value === t) || { label: t, color: 'bg-slate-100 text-slate-700' };
}

function fmtBytes(b) {
  if (!b) return '0 B';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(2) + ' MB';
}

function fmtDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function EmployeeDocsPage({ embeddedEmployeeId = null }) {
  const { user, isAdminLevel, canManageEmployees } = useAuth();
  const canEdit = typeof isAdminLevel === 'function' ? isAdminLevel() : false;
  // Generate Document is open to anyone who can manage employees (Admins + LOP Manager).
  // Destructive / org-wide actions (Upload, Share, Delete, Send-for-Signature) remain admin-only.
  const canGenerate = typeof canManageEmployees === 'function' ? canManageEmployees() : canEdit;

  // Data
  const [docs, setDocs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterEmployee, setFilterEmployee] = useState(embeddedEmployeeId || 'all');
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');

  // Dialogs / panels
  const [uploadOpen, setUploadOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);  // for the rich editor
  const [versionsForDoc, setVersionsForDoc] = useState(null);
  const [versionsList, setVersionsList] = useState([]);
  const [auditForDoc, setAuditForDoc] = useState(null);
  const [auditList, setAuditList] = useState([]);
  const [selected, setSelected] = useState(new Set());
  // Sharing
  const [shareDoc, setShareDoc] = useState(null);
  const [shareUsers, setShareUsers] = useState([]);       // candidates (LOP managers etc.)
  const [shareSelected, setShareSelected] = useState([]);  // user ids currently shared
  const [shareLoading, setShareLoading] = useState(false);
  const [shareSaving, setShareSaving] = useState(false);
  // Send for e-signature (Zoho Sign)
  const [esignDoc, setEsignDoc] = useState(null);
  const [esignEmp, setEsignEmp] = useState('');
  const [esignHR, setEsignHR] = useState('');
  const [esignMgmt, setEsignMgmt] = useState('');
  const [esignSending, setEsignSending] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (embeddedEmployeeId) params.employee_id = embeddedEmployeeId;
      const [d, e, o] = await Promise.all([
        api.get('/employee-documents', { params }),
        embeddedEmployeeId ? Promise.resolve({ data: [] }) : api.get('/employees'),
        embeddedEmployeeId ? Promise.resolve({ data: [] }) : api.get('/organizations'),
      ]);
      setDocs(d.data || []);
      setEmployees(e.data || []);
      setOrgs(o.data || []);
    } catch (err) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [embeddedEmployeeId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Derived: filtered list
  const filtered = useMemo(() => {
    return (docs || []).filter(d => {
      if (filterEmployee !== 'all' && d.employee_id !== filterEmployee) return false;
      if (filterOrg !== 'all' && d.organization_id !== filterOrg) return false;
      if (filterType !== 'all' && d.doc_type !== filterType) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = `${d.title || ''} ${d.employee_name || ''} ${d.file_name || ''} ${d.reference_no || ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [docs, filterEmployee, filterOrg, filterType, search]);

  // KPIs
  const kpis = useMemo(() => {
    const total = docs.length;
    const byType = {};
    DOC_TYPES.forEach(t => { byType[t.value] = 0; });
    const now = new Date();
    let thisMonth = 0;
    docs.forEach(d => {
      byType[d.doc_type] = (byType[d.doc_type] || 0) + 1;
      const dt = new Date(d.created_at);
      if (dt.getFullYear() === now.getFullYear() && dt.getMonth() === now.getMonth()) thisMonth++;
    });
    return { total, byType, thisMonth };
  }, [docs]);

  // Preview handler: if the doc has editable HTML (offer letters), open the rich editor.
  // Otherwise fall back to a read-only PDF iframe modal.
  const handlePreview = async (d) => {
    // Offer letters are generated with html_content; for now we treat that as the
    // 'editable' marker. We don't have html_content in the list payload, so we
    // optimistically open the editor for offer_letter+generated docs and let the
    // GET /html endpoint return 404 if the doc doesn't actually support it.
    const editable = d.doc_type === 'offer_letter' && d.source === 'generated';
    if (editable) {
      setEditingDoc(d);
      return;
    }
    try {
      const res = await api.get(`/employee-documents/${d.id}/pdf?inline=true`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      setPreviewBlobUrl(url);
      setPreviewDoc(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview');
    }
  };

  const closePreview = () => {
    if (previewBlobUrl) window.URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
    setPreviewDoc(null);
  };

  // Download as PDF (ready-to-send). Server converts on-the-fly from the stored DOCX.
  const handleDownload = async (d, format = 'pdf') => {
    try {
      const path = format === 'pdf' ? `/employee-documents/${d.id}/pdf` : `/employee-documents/${d.id}/file`;
      const res = await api.get(path, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      const baseName = (d.file_name || `${d.doc_type}`).replace(/\.[^.]+$/, '');
      a.href = url;
      a.download = format === 'pdf' ? `${baseName}.pdf` : (d.file_name || `${d.doc_type}.bin`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // When responseType is 'blob', error.response.data is also a Blob — parse it
      let detail = e.message || 'Download failed';
      try {
        const blob = e.response?.data;
        if (blob && typeof blob.text === 'function') {
          const txt = await blob.text();
          try { detail = JSON.parse(txt).detail || txt; } catch { detail = txt || detail; }
        }
      } catch (_) { /* ignore parse errors */ }
      if (e.response?.status) detail = `${e.response.status} · ${detail}`;
      toast.error(`Download failed: ${String(detail).slice(0, 220)}`);
    }
  };

  const openVersions = async (d) => {
    try {
      const res = await api.get(`/employee-documents/${d.id}/versions`);
      setVersionsList(res.data || []);
      setVersionsForDoc(d);
    } catch { toast.error('Could not load versions'); }
  };

  const openAudit = async (d) => {
    try {
      const res = await api.get(`/employee-documents/${d.id}/audit`);
      setAuditList(res.data || []);
      setAuditForDoc(d);
    } catch { toast.error('Could not load audit log'); }
  };

  // ─── Sharing ──────────────────────────────────────────────────────────
  const openShare = async (d) => {
    setShareDoc(d);
    setShareLoading(true);
    try {
      const [usersRes, shareRes] = await Promise.all([
        api.get('/users'),
        api.get(`/employee-documents/${d.id}/share`),
      ]);
      // Show non-admin roles only — admins already have full access
      const candidates = (usersRes.data || []).filter(u =>
        !['super_admin', 'admin'].includes(u.role)
      );
      setShareUsers(candidates);
      setShareSelected(shareRes.data?.shared_with || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load share settings');
      setShareDoc(null);
    } finally {
      setShareLoading(false);
    }
  };

  const toggleShareUser = (uid) => {
    setShareSelected(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };

  const saveShare = async () => {
    if (!shareDoc) return;
    setShareSaving(true);
    try {
      await api.put(`/employee-documents/${shareDoc.id}/share`, { user_ids: shareSelected });
      toast.success(shareSelected.length ? `Shared with ${shareSelected.length} user${shareSelected.length > 1 ? 's' : ''}` : 'Sharing cleared');
      setShareDoc(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save sharing');
    } finally {
      setShareSaving(false);
    }
  };

  // ─── Send for e-signature (Zoho Sign) ───────────────────────────────
  const openEsign = (d) => {
    setEsignDoc(d);
    setEsignEmp('');
    setEsignHR('');
    setEsignMgmt('');
  };

  const sendForEsign = async () => {
    if (!esignDoc) return;
    if (!esignEmp || !esignHR || !esignMgmt) {
      toast.error('All 3 signer emails are required');
      return;
    }
    setEsignSending(true);
    try {
      const res = await api.post(`/employee-documents/${esignDoc.id}/send-for-signature`, {
        signer_employee: esignEmp,
        signer_hr: esignHR,
        signer_management: esignMgmt,
        employee_signer_name: esignDoc.employee_name || null,
      });
      toast.success(res.data?.message || 'Document sent for signature');
      setEsignDoc(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send for signature');
    } finally {
      setEsignSending(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/employee-documents/${id}`);
      toast.success('Document deleted');
      loadAll();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleBulkDownload = async () => {
    if (selected.size === 0) { toast.error('Select at least one document'); return; }
    try {
      const res = await api.post('/employee-documents/bulk-download',
        { ids: Array.from(selected) },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employee_documents.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSelected(new Set());
    } catch {
      toast.error('Bulk download failed');
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="employee-docs-page">
        {!embeddedEmployeeId && (
          <div className="flex justify-between items-start gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                <FileBadge className="h-7 w-7 text-emerald-600" /> Employee Docs
              </h1>
              <p className="text-muted-foreground text-sm">
                Offer Letters, Increments, Addendums, NDAs, PIPs and more — uploaded or generated.
              </p>
            </div>
            {(canEdit || canGenerate) && (
              <div className="flex gap-2">
                {canEdit && (
                  <Button variant="outline" onClick={() => setUploadOpen(true)} data-testid="upload-doc-btn">
                    <Upload className="h-4 w-4 mr-1.5" /> Upload Document
                  </Button>
                )}
                {canGenerate && (
                  <Button onClick={() => setGenerateOpen(true)} data-testid="generate-doc-btn">
                    <Sparkles className="h-4 w-4 mr-1.5" /> Generate Document
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* KPIs */}
        {!embeddedEmployeeId && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card><CardContent className="py-3 px-4">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Total Documents</div>
              <div className="text-2xl font-bold mt-0.5" data-testid="kpi-total">{kpis.total}</div>
            </CardContent></Card>
            <Card><CardContent className="py-3 px-4">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide">This Month</div>
              <div className="text-2xl font-bold mt-0.5 text-emerald-600">{kpis.thisMonth}</div>
            </CardContent></Card>
            <Card><CardContent className="py-3 px-4">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Offer Letters</div>
              <div className="text-2xl font-bold mt-0.5 text-emerald-600">{kpis.byType.offer_letter || 0}</div>
            </CardContent></Card>
            <Card><CardContent className="py-3 px-4">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Increments</div>
              <div className="text-2xl font-bold mt-0.5 text-blue-600">{kpis.byType.increment || 0}</div>
            </CardContent></Card>
            <Card><CardContent className="py-3 px-4">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Addendums</div>
              <div className="text-2xl font-bold mt-0.5 text-purple-600">{kpis.byType.addendum || 0}</div>
            </CardContent></Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-8 h-9"
                  placeholder="Search title, employee, file name…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="search-input"
                />
              </div>
              {!embeddedEmployeeId && (
                <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                  <SelectTrigger className="w-[180px] h-9" data-testid="filter-employee"><SelectValue placeholder="Employee" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.employee_id} — {e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!embeddedEmployeeId && (
                <Select value={filterOrg} onValueChange={setFilterOrg}>
                  <SelectTrigger className="w-[180px] h-9" data-testid="filter-org"><SelectValue placeholder="Organization" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {orgs.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[160px] h-9" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {selected.size > 0 && (
                <Button variant="outline" size="sm" onClick={handleBulkDownload} data-testid="bulk-download-btn">
                  <PackageOpen className="h-4 w-4 mr-1.5" /> Download {selected.size} as ZIP
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="px-0 py-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Uploaded By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
                )}
                {!loading && filtered.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">
                    <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
                    No documents yet. {canEdit ? 'Click Upload or Generate to create one.' : canGenerate ? 'Click Generate Document to create one.' : ''}
                  </TableCell></TableRow>
                )}
                {!loading && filtered.map(d => {
                  const tm = typeMeta(d.doc_type);
                  return (
                    <TableRow key={d.id} className="hover:bg-muted/40" data-testid={`doc-row-${d.id}`}>
                      <TableCell>
                        <Checkbox checked={selected.has(d.id)} onCheckedChange={() => toggleSelect(d.id)} />
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={tm.color}>{tm.label}</Badge>
                      </TableCell>
                      <TableCell className="font-medium max-w-xs truncate">{d.title || '—'}</TableCell>
                      <TableCell>
                        <div className="text-sm">{d.employee_name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{d.employee_id_code}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm truncate max-w-[200px] flex items-center gap-1.5">
                          <span className="truncate">{orgs.find(o => o.id === d.organization_id)?.name || '—'}</span>
                          <EpfBadge enabled={orgs.find(o => o.id === d.organization_id)?.epf_enabled} />
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="font-mono">v{d.version || 1}</Badge></TableCell>
                      <TableCell>
                        <span className={`text-[11px] px-2 py-0.5 rounded ${d.source === 'generated' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                          {d.source === 'generated' ? 'Generated' : 'Uploaded'}
                        </span>
                        {d.zoho_request_id && (
                          <Badge variant="outline" className="ml-1 text-[10px] text-blue-700 border-blue-300">e-sign</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{d.created_by}</TableCell>
                      <TableCell className="text-xs font-mono">{fmtDate(d.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-0.5">
                          <Button variant="ghost" size="icon" title="Preview" onClick={() => handlePreview(d)} data-testid={`preview-${d.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Download" onClick={() => handleDownload(d)} data-testid={`download-${d.id}`}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Versions" onClick={() => openVersions(d)} data-testid={`versions-${d.id}`}>
                            <History className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Audit" onClick={() => openAudit(d)} data-testid={`audit-${d.id}`}>
                            <ScrollText className="h-4 w-4" />
                          </Button>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title={d.zoho_request_id
                                ? `Already sent (${d.zoho_status || 'sent'})`
                                : 'Send for e-signature via Zoho Sign'}
                              onClick={() => openEsign(d)}
                              disabled={d.zoho_status === 'sent' || d.zoho_status === 'in_progress'}
                              className={d.zoho_request_id ? 'text-blue-600' : ''}
                              data-testid={`esign-${d.id}`}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                          {canEdit && (
                            <Button variant="ghost" size="icon" title="Share with users" onClick={() => openShare(d)} data-testid={`share-${d.id}`}>
                              <Share2 className="h-4 w-4" />
                            </Button>
                          )}
                          {canEdit && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" title="Delete" className="text-destructive hover:text-destructive" data-testid={`delete-${d.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete "{d.title || d.file_name}"?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will soft-delete the document. Audit history is retained.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(d.id)} className="bg-destructive text-white">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* UPLOAD DIALOG */}
      {canEdit && (
        <UploadDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          employees={employees}
          embeddedEmployeeId={embeddedEmployeeId}
          onCreated={() => { setUploadOpen(false); loadAll(); }}
        />
      )}

      {/* GENERATE DIALOG */}
      {canGenerate && (
        <GenerateDialog
          open={generateOpen}
          onClose={() => setGenerateOpen(false)}
          employees={employees}
          orgs={orgs}
          embeddedEmployeeId={embeddedEmployeeId}
          onCreated={() => { setGenerateOpen(false); loadAll(); }}
        />
      )}

      {/* PREVIEW MODAL */}
      <Dialog open={!!previewDoc} onOpenChange={(v) => { if (!v) closePreview(); }}>
        <DialogContent className="max-w-5xl h-[88vh] p-0 flex flex-col">
          <DialogHeader className="px-5 py-3 border-b">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <DialogTitle className="flex items-center gap-2">
                  <Eye className="h-4 w-4" /> {previewDoc?.title}
                  <Badge variant="outline" className="ml-1 text-[10px]">{typeMeta(previewDoc?.doc_type).label}</Badge>
                  <Badge variant="secondary" className="ml-1 text-[10px] font-mono">v{previewDoc?.version}</Badge>
                </DialogTitle>
                <DialogDescription className="text-xs mt-1">
                  {previewDoc?.file_name} · {fmtBytes(previewDoc?.file_size)} · {fmtDate(previewDoc?.created_at)}
                </DialogDescription>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" onClick={() => handleDownload(previewDoc, 'pdf')} data-testid="preview-download-pdf" className="bg-emerald-600 hover:bg-emerald-700">
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Download as PDF
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 bg-slate-100 overflow-hidden">
            {previewBlobUrl ? (
              <iframe src={previewBlobUrl} title="preview" className="w-full h-full border-0" />
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
                <div className="animate-pulse text-sm text-muted-foreground">Loading preview…</div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* VERSIONS PANEL */}
      <Sheet open={!!versionsForDoc} onOpenChange={(v) => { if (!v) setVersionsForDoc(null); }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><History className="h-4 w-4" /> Version History</SheetTitle>
            <SheetDescription>{versionsForDoc?.title} · {versionsForDoc?.employee_name}</SheetDescription>
          </SheetHeader>
          <div className="space-y-2 mt-4">
            {versionsList.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">No previous versions</div>
            )}
            {versionsList.map(v => (
              <Card key={v.id} className={v.is_latest ? 'border-emerald-300' : ''}>
                <CardContent className="py-3 px-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="font-mono">v{v.version}</Badge>
                      {v.is_latest && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Latest</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">{fmtDate(v.created_at)}</div>
                    <div className="text-xs text-muted-foreground">by {v.created_by}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handlePreview(v)} title="Preview"><Eye className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDownload(v)} title="Download"><Download className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* AUDIT PANEL */}
      <Sheet open={!!auditForDoc} onOpenChange={(v) => { if (!v) setAuditForDoc(null); }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><ScrollText className="h-4 w-4" /> Audit Log</SheetTitle>
            <SheetDescription>{auditForDoc?.title}</SheetDescription>
          </SheetHeader>
          <div className="space-y-1 mt-4">
            {auditList.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">No activity recorded</div>
            )}
            {auditList.map(a => (
              <div key={a.id} className="flex items-start gap-3 py-2 border-b last:border-b-0">
                <div className={`w-2 h-2 rounded-full mt-2 ${a.action === 'delete' ? 'bg-red-500' : a.action === 'generate' || a.action === 'upload' ? 'bg-emerald-500' : 'bg-blue-500'}`}></div>
                <div className="flex-1">
                  <div className="text-sm capitalize font-medium">{a.action}</div>
                  <div className="text-xs text-muted-foreground">{a.user_name} ({a.user_role})</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{fmtDate(a.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Rich Document Editor (Offer Letters) */}
      {editingDoc && (
        <DocumentEditor
          doc={editingDoc}
          onClose={() => setEditingDoc(null)}
          onSaved={() => { loadAll(); }}
        />
      )}

      {/* SHARE DIALOG (Admin only) */}
      <Dialog open={!!shareDoc} onOpenChange={(v) => { if (!v) setShareDoc(null); }}>
        <DialogContent className="max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-4 w-4 text-emerald-600" /> Share Document
            </DialogTitle>
            <DialogDescription className="truncate">
              Grant view &amp; download access to {shareDoc?.title || 'this document'}.
            </DialogDescription>
          </DialogHeader>

          {shareLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Loading users…</div>
          ) : (
            <div className="space-y-3">
              <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                Super Admin &amp; Admin always have access. Pick additional users below.
              </div>

              {shareUsers.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center border rounded">
                  No other users to share with yet. Create users from the Admin → Users page.
                </div>
              ) : (
                <div className="max-h-[320px] overflow-y-auto border rounded divide-y">
                  {shareUsers.map(u => (
                    <label
                      key={u.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-muted/40 cursor-pointer"
                      data-testid={`share-user-${u.id}`}
                    >
                      <Checkbox
                        checked={shareSelected.includes(u.id)}
                        onCheckedChange={() => toggleShareUser(u.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{u.email}</div>
                      </div>
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {String(u.role || '').replace('_', ' ')}
                      </Badge>
                    </label>
                  ))}
                </div>
              )}

              <div className="text-[11px] text-muted-foreground">
                {shareSelected.length === 0
                  ? 'No additional users selected — document will be visible to admins only.'
                  : `${shareSelected.length} user${shareSelected.length > 1 ? 's' : ''} selected.`}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDoc(null)} data-testid="share-cancel-btn">Cancel</Button>
            <Button
              onClick={saveShare}
              disabled={shareLoading || shareSaving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="share-save-btn"
            >
              {shareSaving ? 'Saving…' : 'Save sharing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SEND FOR E-SIGNATURE DIALOG (Admin only) */}
      <Dialog open={!!esignDoc} onOpenChange={(v) => { if (!v && !esignSending) setEsignDoc(null); }}>
        <DialogContent className="max-w-lg p-5 gap-3" data-testid="esign-dialog">
          <DialogHeader className="space-y-1">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Send className="h-4 w-4 text-blue-600" /> Send for e-signature
            </DialogTitle>
            <DialogDescription className="text-[11px] truncate">
              {esignDoc?.title || 'Document'} · sequential signing via Zoho Sign
            </DialogDescription>
          </DialogHeader>

          <div className="text-[10.5px] text-blue-700 rounded bg-blue-50 border border-blue-200 px-2 py-1.5 flex items-center gap-1.5 leading-snug">
            <Send className="h-3 w-3 shrink-0" />
            <span><strong>Employee</strong> → <strong>HR</strong> → <strong>Management</strong>. Each gets a Zoho Sign email.</span>
          </div>

          <div className="space-y-2.5">
            <div className="space-y-1">
              <Label className="text-[11px]">Employee email</Label>
              <Input
                type="email"
                value={esignEmp}
                onChange={(e) => setEsignEmp(e.target.value)}
                placeholder="employee@example.com"
                className="h-8 text-sm"
                data-testid="esign-emp-input"
              />
              {esignDoc?.employee_name && (
                <p className="text-[10px] text-muted-foreground">Signer: <strong>{esignDoc.employee_name}</strong></p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[11px]">HR email</Label>
                <Input
                  type="email"
                  value={esignHR}
                  onChange={(e) => setEsignHR(e.target.value)}
                  placeholder="hr@yourcompany.com"
                  className="h-8 text-sm"
                  data-testid="esign-hr-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Management email</Label>
                <Input
                  type="email"
                  value={esignMgmt}
                  onChange={(e) => setEsignMgmt(e.target.value)}
                  placeholder="ceo@yourcompany.com"
                  className="h-8 text-sm"
                  data-testid="esign-mgmt-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => setEsignDoc(null)} disabled={esignSending} data-testid="esign-cancel-btn">Cancel</Button>
            <Button
              size="sm"
              onClick={sendForEsign}
              disabled={esignSending || !esignEmp || !esignHR || !esignMgmt}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="esign-send-btn"
            >
              {esignSending ? 'Sending…' : 'Send to Zoho Sign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

// ─────────── Upload Dialog ────────────
function UploadDialog({ open, onClose, employees, embeddedEmployeeId, onCreated }) {
  const [employeeId, setEmployeeId] = useState(embeddedEmployeeId || '');
  const [docType, setDocType] = useState('');
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');
  const [refNo, setRefNo] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setEmployeeId(embeddedEmployeeId || '');
      setDocType('');
      setTitle('');
      setTags('');
      setNotes('');
      setRefNo('');
      setFile(null);
    }
  }, [open, embeddedEmployeeId]);

  const handleSubmit = async () => {
    if (!employeeId || !docType || !file) {
      toast.error('Employee, type and file are required'); return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('employee_id', employeeId);
      fd.append('doc_type', docType);
      if (title) fd.append('title', title);
      if (tags) fd.append('tags', tags);
      if (notes) fd.append('notes', notes);
      if (refNo) fd.append('reference_no', refNo);
      fd.append('file', file);
      await api.post('/employee-documents/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="h-4 w-4" /> Upload Document</DialogTitle>
          <DialogDescription>Attach an existing PDF or DOCX to an employee record (max 5 MB).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!embeddedEmployeeId && (
            <div className="space-y-1.5">
              <Label className="text-xs">Employee *</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger data-testid="upload-emp-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.employee_id} — {e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Document Type *</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="upload-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Title <span className="text-muted-foreground">(optional — defaults to file name)</span></Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Offer Letter — Jainender Kumar" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Reference No.</Label>
              <Input value={refNo} onChange={e => setRefNo(e.target.value)} placeholder="BLG/OL/2026/001" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tags <span className="text-muted-foreground">(comma-separated)</span></Label>
              <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="2026, signed" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">File * (PDF / DOCX)</Label>
            <Input
              type="file"
              accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
              onChange={e => setFile(e.target.files?.[0] || null)}
              data-testid="upload-file-input"
            />
            {file && <div className="text-xs text-muted-foreground">{file.name} · {fmtBytes(file.size)}</div>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any internal notes…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="upload-submit-btn">
            {loading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── Generate Dialog ────────────
function GenerateDialog({ open, onClose, employees, orgs, embeddedEmployeeId, onCreated }) {
  // Mode: 'employee' (existing) vs 'candidate' (new hire — not in employees DB yet)
  const [mode, setMode] = useState(embeddedEmployeeId ? 'employee' : 'employee');
  const [employeeId, setEmployeeId] = useState(embeddedEmployeeId || '');
  const [organizationId, setOrganizationId] = useState('');
  const [docType, setDocType] = useState('offer_letter');
  const [candidateName, setCandidateName] = useState('');
  const [designation, setDesignation] = useState('');
  const [monthlyGross, setMonthlyGross] = useState('');
  const [retentionBonus, setRetentionBonus] = useState('');
  const [annualBonus, setAnnualBonus] = useState('');
  const [doj, setDoj] = useState('');
  const [refNo, setRefNo] = useState('');
  const [workLocation, setWorkLocation] = useState('Flexible');
  const [workingDays, setWorkingDays] = useState('Monday to Saturday');
  const [reportingManager, setReportingManager] = useState('Concerned Reporting Manager / Department Head');
  const [probation, setProbation] = useState('6 months');
  const [noticePeriod, setNoticePeriod] = useState('30 days during probation / 60 days post-confirmation');
  const [rolesResponsibilities, setRolesResponsibilities] = useState('');
  const [previousGross, setPreviousGross] = useState('');
  const [reason, setReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [sendForSign, setSendForSign] = useState(false);
  const [signerEmp, setSignerEmp] = useState('');
  const [signerHR, setSignerHR] = useState('');
  const [signerMgmt, setSignerMgmt] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(embeddedEmployeeId ? 'employee' : 'employee');
    setDocType('offer_letter');
    setMonthlyGross(''); setRetentionBonus(''); setAnnualBonus('');
    setDoj(''); setRefNo(''); setPreviousGross(''); setReason(''); setEffectiveDate('');
    setSendForSign(false); setSignerEmp(''); setSignerHR(''); setSignerMgmt('');
    setEmployeeId(embeddedEmployeeId || '');
    setOrganizationId('');
    setCandidateName('');
    setDesignation('');
    setRolesResponsibilities('');
  }, [open, embeddedEmployeeId]);

  // Auto-fill from existing employee
  useEffect(() => {
    if (mode !== 'employee' || !employeeId) return;
    const emp = employees.find(e => e.id === employeeId);
    if (emp) {
      setCandidateName(emp.name || '');
      setDesignation(emp.designation || '');
      const gross = emp.gross_salary || emp.monthly_salary || 0;
      if (docType === 'increment' || docType === 'addendum') {
        setPreviousGross(gross ? String(gross) : '');
      } else {
        setMonthlyGross(gross ? String(gross) : '');
      }
    }
  }, [employeeId, employees, orgs, docType, mode]);

  // When switching to candidate mode, clear employee-derived fields
  useEffect(() => {
    if (mode === 'candidate') {
      setEmployeeId('');
      setCandidateName('');
      setDesignation('');
      setMonthlyGross('');
      setPreviousGross('');
      setRefNo('');  // refetch a fresh reference number after switching
    }
  }, [mode]);

  // Auto-fetch next sequential reference number when org + doc type are known
  useEffect(() => {
    if (!open || !docType) return;
    let orgId = null;
    if (mode === 'candidate') {
      orgId = organizationId;
    } else if (employeeId) {
      const emp = employees.find(e => e.id === employeeId);
      orgId = emp?.organization_id;
    }
    if (!orgId) return;
    // Don't override a manually-entered ref no.
    if (refNo) return;
    let cancelled = false;
    api.get('/employee-documents/next-reference', { params: { organization_id: orgId, doc_type: docType } })
      .then(res => { if (!cancelled) setRefNo(res.data?.reference_no || ''); })
      .catch(() => { /* silent — user can type manually */ });
    return () => { cancelled = true; };
  }, [open, mode, organizationId, employeeId, employees, docType, refNo]);

  const handleSubmit = async () => {
    if (!docType) { toast.error('Document type required'); return; }
    if (!GENERATABLE.includes(docType)) { toast.error('Generation not supported for this type yet — please use Upload'); return; }
    if (mode === 'employee' && !employeeId) { toast.error('Please pick an employee'); return; }
    if (mode === 'candidate') {
      if (!organizationId) { toast.error('Please pick the organization'); return; }
      if (!candidateName.trim()) { toast.error('Candidate name is required'); return; }
      if (docType !== 'offer_letter') { toast.error('New Candidate mode currently supports Offer Letters only'); return; }
    }
    if (docType === 'offer_letter' && !monthlyGross) { toast.error('Monthly Gross is required'); return; }
    if ((docType === 'increment' || docType === 'addendum') && !monthlyGross) { toast.error('New Gross is required'); return; }
    if (sendForSign && (!signerEmp || !signerHR || !signerMgmt)) { toast.error('All 3 signer emails required'); return; }
    setLoading(true);
    try {
      const body = {
        employee_id: mode === 'employee' ? employeeId : null,
        organization_id: mode === 'candidate' ? organizationId : null,
        doc_type: docType,
        candidate_name: candidateName || null,
        designation: designation || null,
        monthly_gross: parseFloat(monthlyGross) || 0,
        retention_bonus: parseFloat(retentionBonus) || 0,
        annual_bonus: parseFloat(annualBonus) || 0,
        date_of_joining: doj || null,
        reference_no: refNo || null,
        work_location: workLocation,
        working_days: workingDays,
        reporting_manager: reportingManager,
        probation_period: probation,
        notice_period: noticePeriod,
        roles_responsibilities: rolesResponsibilities || null,
        previous_gross: parseFloat(previousGross) || null,
        new_gross: parseFloat(monthlyGross) || null,
        reason: reason || null,
        effective_date: effectiveDate || null,
        send_for_signature: !!sendForSign,
        signer_employee: signerEmp || null,
        signer_hr: signerHR || null,
        signer_management: signerMgmt || null,
        epf_enabled: true,
      };
      await api.post('/employee-documents/generate', body);
      toast.success(sendForSign ? 'Generated & sent for e-signature' : 'Document generated');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const isIncrement = (docType === 'increment' || docType === 'addendum');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-emerald-600" /> Generate Document</DialogTitle>
          <DialogDescription>System-generated document with the same template as Salary Slip. Currently supports Offer Letter, Increment, Addendum.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          {/* Mode toggle: Existing Employee vs New Candidate */}
          {!embeddedEmployeeId && (
            <div className="col-span-2 flex items-center gap-2 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-md p-1" data-testid="gen-mode-toggle">
              <button
                type="button"
                onClick={() => setMode('employee')}
                className={`flex-1 py-1.5 px-3 text-xs font-medium rounded transition ${mode === 'employee' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="mode-employee-btn"
              >
                Existing Employee
              </button>
              <button
                type="button"
                onClick={() => setMode('candidate')}
                className={`flex-1 py-1.5 px-3 text-xs font-medium rounded transition ${mode === 'candidate' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="mode-candidate-btn"
              >
                New Candidate
              </button>
            </div>
          )}
          {!embeddedEmployeeId && mode === 'employee' && (
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Employee *</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger data-testid="gen-emp-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.employee_id} — {e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {!embeddedEmployeeId && mode === 'candidate' && (
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Organization *</Label>
              <Select value={organizationId} onValueChange={setOrganizationId}>
                <SelectTrigger data-testid="gen-org-select"><SelectValue placeholder="Select the hiring organization" /></SelectTrigger>
                <SelectContent>
                  {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">For new hires not yet added to your Employees list. The candidate doesn't need to exist in the system.</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Document Type *</Label>
            <Select value={docType} onValueChange={(v) => { setDocType(v); setRefNo(''); }}>
              <SelectTrigger data-testid="gen-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.filter(t => GENERATABLE.includes(t.value)).map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              Reference No.
              <span className="text-[10px] font-normal text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">auto</span>
            </Label>
            <Input
              value={refNo}
              onChange={e => setRefNo(e.target.value)}
              placeholder="Auto-generated when you pick org + type"
              className="font-mono text-sm"
              data-testid="gen-refno-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Candidate / Employee Name {mode === 'candidate' ? '*' : ''}</Label>
            <Input value={candidateName} onChange={e => setCandidateName(e.target.value)} placeholder={mode === 'candidate' ? 'e.g. Jainender Kumar' : 'Auto-filled from employee'} data-testid="gen-candidate-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Designation</Label>
            <Input value={designation} onChange={e => setDesignation(e.target.value)} placeholder="e.g. Software Engineer" />
          </div>

          {isIncrement && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Previous Monthly Gross (₹)</Label>
                <Input type="number" value={previousGross} onChange={e => setPreviousGross(e.target.value)} className="font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">New Monthly Gross * (₹)</Label>
                <Input type="number" value={monthlyGross} onChange={e => setMonthlyGross(e.target.value)} className="font-mono" data-testid="gen-gross-input" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Reason</Label>
                <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Annual revision, role change, retention…" />
              </div>
            </>
          )}

          {!isIncrement && (
            <div className="space-y-1.5">
              <Label className="text-xs">Monthly Gross * (₹)</Label>
              <Input type="number" value={monthlyGross} onChange={e => setMonthlyGross(e.target.value)} className="font-mono" data-testid="gen-gross-input" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Retention Bonus (₹/mo)</Label>
            <Input type="number" value={retentionBonus} onChange={e => setRetentionBonus(e.target.value)} className="font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Annual Bonus (₹/yr)</Label>
            <Input type="number" value={annualBonus} onChange={e => setAnnualBonus(e.target.value)} className="font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{isIncrement ? 'Effective Date' : 'Date of Joining'}</Label>
            <Input
              type="date"
              value={isIncrement ? effectiveDate : doj}
              onChange={e => isIncrement ? setEffectiveDate(e.target.value) : setDoj(e.target.value)}
              data-testid="gen-date-input"
            />
          </div>

          {docType === 'offer_letter' && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Work Location</Label>
                <Input value={workLocation} onChange={e => setWorkLocation(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Working Days</Label>
                <Input value={workingDays} onChange={e => setWorkingDays(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Reporting Manager</Label>
                <Input value={reportingManager} onChange={e => setReportingManager(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Probation Period</Label>
                <Input value={probation} onChange={e => setProbation(e.target.value)} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs flex items-center justify-between">
                  <span>Roles &amp; Responsibilities</span>
                  <span className="text-[10px] text-muted-foreground font-normal">Optional · appears below the compensation table</span>
                </Label>
                <Textarea
                  value={rolesResponsibilities}
                  onChange={e => setRolesResponsibilities(e.target.value)}
                  rows={5}
                  placeholder={'e.g.\n• Plan, execute and optimize digital marketing campaigns (SEO, SEM, social).\n• Track KPIs and report weekly performance to the leadership team.\n• Collaborate cross-functionally with sales, design and product.'}
                  className="font-mono text-sm"
                  data-testid="roles-responsibilities-textarea"
                />
              </div>
            </>
          )}
        </div>

        <div className="border-t pt-3 mt-2">
          <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
            <Send className="h-3 w-3 mt-0.5 shrink-0" />
            <span>After generating, click the <strong>Send for e-signature</strong> icon in the Actions column to dispatch this document via Zoho Sign.</span>
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="gen-submit-btn">
            {loading ? 'Generating…' : 'Generate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
